import { createContext, memo, useContext, useEffect, useId, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { sectionMarkdown, streamingMarkdown } from '../utils/markdownSource';
import { hastText, headingLink, REMARK_PLUGINS, rehypeAlignNumbers, rehypeFootnotePending, rehypeHeadingIds, rehypeMathPending } from '../utils/markdownPlugins';
import { CALLOUT_LABELS as CALLOUTS } from '../utils/callouts.js';
import { readTable, toCSV, toMarkdownTable, toTSV } from '../utils/tableExport';
import { statusTone } from '../utils/statusTone';
import { useCopy } from '../hooks/useCopy';
import { Button } from './Button';
import { CopyButton } from './CopyButton';
import { HighlightedCode } from './HighlightedCode';
import { StatusPill } from './StatusPill';
import { OpenArtifactButton } from './artifacts/ArtifactContext';
import { useToast } from './Toast';
import { AlertIcon, CheckIcon, ChevronIcon, CopyIcon, FileTextIcon, InfoIcon, LinkIcon, SparkIcon } from './icons';
import { FichiersContext } from './fichiers/FichiersContext.js';
import { cleUrl, lienTelechargement, lienVerifie } from './fichiers/fichiers.js';
import { nomDeTelechargement } from './fichiers/photosSpec.js';
import './MarkdownRenderer.css';
import './fichiers/Fichiers.css';

// Texte source et état du streaming, lus par les composants de rendu ; images : photos des liens
// vérifiés recopiés dans ce message (visionneuse : photo précédente et suivante)
const MarkdownContext = createContext({ source: '', isStreaming: false, idPrefix: 'md', images: [] });

// errorColor : KaTeX pose la couleur d'erreur en style en ligne (rouge vif par défaut,
// illisible en sombre) ; le jeton du thème la remplace
const KATEX_PLUGIN = [rehypeKatex, {
  throwOnError: false,
  strict: 'ignore',
  output: 'htmlAndMathml',
  errorColor: 'var(--ds-danger-fg)',
}];

// ── Blocs de code ───────────────────────────────────────────────────
function CodeBlock({ code, language, offset }) {
  const { idPrefix, isStreaming } = useContext(MarkdownContext);
  const artifactId = `${idPrefix}:code:${offset ?? 0}`;

  return (
    <div className="code-block">
      <div className="code-header">
        <span className="code-language">{language || 'texte'}</span>
        <div className="code-actions">
          {/* Pendant le streaming, le bloc change encore : pas d'ouverture dans le panneau */}
          {!isStreaming && (
            <OpenArtifactButton
              artifactId={artifactId}
              label="Ouvrir le code dans le panneau"
              showLabel
              className="code-open"
              build={() => ({
                id: artifactId,
                type: 'code',
                title: `Code ${language || 'texte'}`,
                subtitle: `${code.split('\n').length} ligne${code.includes('\n') ? 's' : ''}`,
                messageId: idPrefix,
                payload: { code, language },
              })}
            />
          )}
          <CopyButton
            getText={() => code}
            label="Copier le code"
            showLabel
            className="code-copy"
            size={14}
          />
        </div>
      </div>
      <pre tabIndex={0} aria-label={`Code ${language || ''}`.trim()}>
        <HighlightedCode code={code} language={language} />
      </pre>
    </div>
  );
}

function Pre({ node, children, ...props }) {
  const codeNode = node?.children?.find((child) => child.type === 'element');
  if (codeNode?.tagName !== 'code') return <pre {...props}>{children}</pre>;

  const classes = [].concat(codeNode.properties?.className ?? []).map(String);
  const language = classes.find((c) => c.startsWith('language-'))?.slice(9) ?? '';
  const code = hastText(codeNode).replace(/\n$/, '');
  return <CodeBlock code={code} language={language} offset={node?.position?.start?.offset} />;
}

// ── Tableaux : défilement horizontal, en-tête collant, copie TSV / Markdown / CSV ──
const TABLE_FORMATS = [
  { id: 'tsv', label: 'Pour Excel (TSV)', convert: toTSV },
  { id: 'markdown', label: 'Markdown', convert: toMarkdownTable },
  { id: 'csv', label: 'CSV', convert: toCSV },
];

function TableCopyMenu({ tableRef }) {
  const [open, setOpen] = useState(false);
  const { copied, copy } = useCopy();
  const toast = useToast();
  const rootRef = useRef(null);
  const itemsRef = useRef([]);

  useEffect(() => {
    if (!open) return undefined;
    itemsRef.current[0]?.focus();
    const close = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [open]);

  const choose = async (format) => {
    setOpen(false);
    rootRef.current?.querySelector('.table-copy-trigger')?.focus();
    if (!tableRef.current) return;
    const ok = await copy(format.convert(readTable(tableRef.current)));
    if (!ok) toast('Copie impossible', { tone: 'error' });
  };

  const onMenuKeyDown = (event) => {
    const items = itemsRef.current.filter(Boolean);
    const index = items.indexOf(document.activeElement);
    if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
      rootRef.current?.querySelector('.table-copy-trigger')?.focus();
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      items[(index + 1) % items.length]?.focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      items[(index - 1 + items.length) % items.length]?.focus();
    } else if (event.key === 'Tab') {
      setOpen(false);
    }
  };

  return (
    <div className="table-copy" ref={rootRef}>
      <Button
        variant="ghost"
        size="sm"
        icon={copied ? CheckIcon : CopyIcon}
        className={`copy-button table-copy-trigger ${copied ? 'is-copied' : ''}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' && !open) {
            event.preventDefault();
            setOpen(true);
          }
        }}
        title="Copier le tableau"
      >
        <span>{copied ? 'Copié' : 'Copier le tableau'}</span>
        <ChevronIcon size={12} className="table-copy-chevron" />
        <span className="sr-only" aria-live="polite">{copied ? 'Tableau copié' : ''}</span>
      </Button>
      {open && (
        <div className="table-copy-menu" role="menu" aria-label="Format de copie" onKeyDown={onMenuKeyDown}>
          {TABLE_FORMATS.map((format, i) => (
            <button
              key={format.id}
              type="button"
              role="menuitem"
              ref={(el) => { itemsRef.current[i] = el; }}
              tabIndex={-1}
              onClick={() => choose(format)}
            >
              {format.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Table({ node, children, ...props }) {
  const tableRef = useRef(null);
  const { idPrefix, isStreaming } = useContext(MarkdownContext);
  const artifactId = `${idPrefix}:table:${node?.position?.start?.offset ?? 0}`;

  // Cellules lues telles qu'affichées (formules KaTeX remplacées par leur source)
  const buildArtifact = () => {
    if (!tableRef.current) return null;
    const rows = readTable(tableRef.current);
    const count = Math.max(0, rows.length - 1);
    return {
      id: artifactId,
      type: 'table',
      title: 'Tableau',
      subtitle: `${count} ligne${count > 1 ? 's' : ''} · ${rows[0]?.length ?? 0} colonnes`,
      messageId: idPrefix,
      payload: { rows },
    };
  };

  return (
    <div className="md-table">
      <div className="md-table-toolbar">
        {!isStreaming && (
          <OpenArtifactButton
            artifactId={artifactId}
            label="Ouvrir le tableau dans le panneau (tri, filtre, export)"
            showLabel
            build={buildArtifact}
          />
        )}
        <TableCopyMenu tableRef={tableRef} />
      </div>
      <div className="md-table-scroll" tabIndex={0} role="region" aria-label="Tableau défilant">
        <table ref={tableRef} {...props}>{children}</table>
      </div>
    </div>
  );
}

// Cellule du corps : un statut du dictionnaire fermé (« En panne », « Disponible »…)
// s'affiche en pastille, texte inchangé (copie et export lisent le même texte)
function TableCell({ node, children, ...props }) {
  const tone = statusTone(hastText(node).trim());
  return <td {...props}>{tone ? <StatusPill tone={tone}>{children}</StatusPill> : children}</td>;
}

// ── Titres de section (h2 / h3) : ancre, copie du lien et du Markdown de la section ──
function makeHeading(level) {
  const Tag = `h${level}`;
  return function SectionHeading({ node, children, id, ...props }) {
    const { source, isStreaming } = useContext(MarkdownContext);
    // Nom du titre limité à son texte : les boutons n'y sont pas lus (liste des titres).
    // useId : jamais l'ancre d'un autre titre (« Synthèse » / « Synthèse texte »)
    const textId = useId();
    const start = node?.position?.start?.offset;
    if (!id || start == null) {
      return <Tag className="md-heading" id={id} {...props}>{children}</Tag>;
    }
    // Boutons présents dès le streaming, masqués (ni visibles, ni focusables, ni lus) : le
    // titre a sa hauteur définitive et le texte qui suit ne bouge pas à la fin du flux
    return (
      <Tag className="md-heading" id={id} aria-labelledby={textId} {...props}>
        <span id={textId}>{children}</span>
        <span className={`md-heading-actions ${isStreaming ? 'is-pending' : ''}`.trim()} aria-hidden={isStreaming || undefined}>
          <CopyButton
            className="md-section-copy"
            label="Copier le lien"
            icon={LinkIcon}
            size={14}
            getText={() => headingLink(id)}
          />
          <CopyButton
            className="md-section-copy"
            label="Copier la section"
            size={14}
            getText={() => sectionMarkdown(source, start, level)}
          />
        </span>
      </Tag>
    );
  };
}

// ── Liens vérifiés (contrat v1.13) : photo ou fichier vu par le serveur dans un résultat d'outil ──
// Seule une adresse identique à celle d'un lien vérifié de la conversation est chargée, et par
// son lien du même domaine (/api/fichiers/<jeton>) : vignette qui ouvre la visionneuse ; un PDF
// devient un lien de téléchargement. Toute autre adresse garde le traitement d'avant.
function VerifiedPhoto({ fichier, legende = '', alt = '' }) {
  const { ouvrir } = useContext(FichiersContext);
  const { images } = useContext(MarkdownContext);
  const [casse, setCasse] = useState(false);
  const label = legende.trim() || fichier.libelle;
  const description = alt.trim() || (fichier.vehicule ? `${fichier.libelle} du véhicule ${fichier.vehicule}` : fichier.libelle);
  const open = () => {
    const index = images.findIndex((it) => it.lien === fichier.lien);
    ouvrir(index >= 0 ? images : [{ lien: fichier.lien, libelle: fichier.libelle, alt: description, vehicule: fichier.vehicule || '' }], Math.max(0, index));
  };
  return (
    <button type="button" className="md-photo" onClick={ouvrir ? open : undefined} disabled={!ouvrir} aria-label={`Agrandir : ${description}`}>
      {casse ? (
        <span className="md-photo__cassee">Photo indisponible</span>
      ) : (
        <img className="md-photo__image" src={fichier.lien} alt={description} loading="lazy" decoding="async" onError={() => setCasse(true)} />
      )}
      <span className="md-photo__legende">{label}</span>
    </button>
  );
}

function VerifiedFile({ fichier, children }) {
  return (
    <a className="md-file-link" href={lienTelechargement(fichier.lien, nomDeTelechargement(fichier.libelle, fichier.vehicule || ''))} download="">
      <FileTextIcon size={14} />
      {children ?? fichier.libelle}
    </a>
  );
}

// Photos des liens vérifiés écrits dans le message, dans leur ordre d'apparition (sans doublon)
const URL_RE = /https?:\/\/[^\s<>()"'[\]]+/g;
function verifiedImages(source, liens) {
  if (!liens?.size || !source) return [];
  const out = [];
  const seen = new Set();
  for (const m of source.matchAll(URL_RE)) {
    const f = lienVerifie(liens, m[0]);
    if (!f || f.type !== 'image' || seen.has(f.lien)) continue;
    seen.add(f.lien);
    out.push({ lien: f.lien, libelle: f.libelle, alt: f.vehicule ? `${f.libelle} du véhicule ${f.vehicule}` : f.libelle, vehicule: f.vehicule || '' });
  }
  return out;
}

// ── Liens : nouvel onglet, sans accès à la page d'origine ────────────
function Link({ node, href = '', children, ...props }) {
  const { liens } = useContext(FichiersContext);
  const fichier = lienVerifie(liens, href);
  if (fichier) {
    if (fichier.type === 'pdf') return <VerifiedFile fichier={fichier}>{children}</VerifiedFile>;
    // Texte du lien (ou texte alternatif de l'image qu'il contient) : légende de la vignette ;
    // une adresse brute (lien automatique) prend le libellé du lien vérifié
    const image = node?.children?.find((c) => c.type === 'element' && c.tagName === 'img');
    const text = (image ? String(image.properties?.alt ?? '') : hastText(node)).trim();
    return <VerifiedPhoto fichier={fichier} legende={cleUrl(text) ? '' : text} />;
  }
  const isInternal = href.startsWith('#');
  return (
    <a
      href={href}
      {...props}
      {...(isInternal ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
    >
      {children}
    </a>
  );
}

// ── Images : jamais chargées ────────────────────────────────────────
// Un texte issu des données (incident, commentaire…) peut pousser le modèle à écrire
// ![](https://site/?d=…) : le navigateur enverrait la requête dès l'affichage, sans
// clic. Le modèle ne produit pas d'image (contrat C0) : seul le texte alternatif reste.
function BlockedImage({ alt }) {
  return alt ? <span className="md-image-alt">[{alt}]</span> : null;
}

// Exception (v1.13) : l'adresse exacte d'une photo vue par le serveur dans un résultat d'outil de
// la conversation, chargée par son lien du même domaine (jamais par l'adresse d'origine)
function MarkdownImage({ src = '', alt = '' }) {
  const { liens } = useContext(FichiersContext);
  const fichier = lienVerifie(liens, src);
  if (!fichier) return <BlockedImage alt={alt} />;
  if (fichier.type === 'pdf') return <VerifiedFile fichier={fichier}>{alt || fichier.libelle}</VerifiedFile>;
  return <VerifiedPhoto fichier={fichier} legende={alt} alt={alt} />;
}

function Blockquote({ node, children, ...props }) {
  const kind = node?.properties?.dataCallout;
  if (!kind || !CALLOUTS[kind]) return <blockquote {...props}>{children}</blockquote>;
  const Icon = kind === 'warning' || kind === 'caution' ? AlertIcon : kind === 'tip' ? SparkIcon : InfoIcon;
  return (
    <aside className={`callout callout--${kind}`} role="note" aria-label={CALLOUTS[kind]}>
      <p className="callout-title">
        <Icon size={15} />
        {CALLOUTS[kind]}
      </p>
      {children}
    </aside>
  );
}

const COMPONENTS = {
  pre: Pre,
  table: Table,
  td: TableCell,
  a: Link,
  blockquote: Blockquote,
  img: MarkdownImage,
  h2: makeHeading(2),
  h3: makeHeading(3),
};

/**
 * Rendu Markdown des réponses : GFM, formules KaTeX, code coloré, copie par bloc.
 * Pas de HTML brut (pas de rehype-raw), pas d'image chargée hors des liens vérifiés de la
 * conversation (FichiersContext, contrat v1.13).
 */
export const MarkdownRenderer = memo(function MarkdownRenderer({ content, isStreaming = false, idPrefix = 'md' }) {
  const { liens } = useContext(FichiersContext);
  // Pendant le streaming : rendu stable (lignes ambiguës retenues, blocs ouverts fermés
  // provisoirement, formule $$ inachevée remplacée par un espace réservé)
  const { source, pendingMath } = useMemo(
    () => (isStreaming ? streamingMarkdown(content) : { source: content, pendingMath: false }),
    [content, isStreaming]
  );
  const images = useMemo(() => verifiedImages(content, liens), [content, liens]);
  const context = useMemo(() => ({ source: content, isStreaming, idPrefix, images }), [content, isStreaming, idPrefix, images]);
  const remarkRehypeOptions = useMemo(() => ({
    // Identifiants des notes de bas de page uniques par message
    clobberPrefix: `${idPrefix}-`,
    footnoteLabel: 'Notes',
    footnoteBackLabel: 'Retour au texte',
  }), [idPrefix]);
  const rehypePlugins = useMemo(() => [
    rehypeAlignNumbers,
    // Ancres des titres uniques par message
    [rehypeHeadingIds, { prefix: idPrefix }],
    ...(pendingMath ? [rehypeMathPending] : []),
    // Renvois de note en attente de leur définition : déjà en exposant pendant le flux
    ...(isStreaming ? [rehypeFootnotePending] : []),
    KATEX_PLUGIN,
  ], [idPrefix, pendingMath, isStreaming]);

  return (
    <MarkdownContext.Provider value={context}>
      <div className="md">
        <ReactMarkdown
          remarkPlugins={REMARK_PLUGINS}
          rehypePlugins={rehypePlugins}
          remarkRehypeOptions={remarkRehypeOptions}
          components={COMPONENTS}
        >
          {source}
        </ReactMarkdown>
      </div>
    </MarkdownContext.Provider>
  );
});
